import React, { useMemo } from "react"
import { usePageContext } from "vike-react/usePageContext"
import ProductBox from "./ProductBox"
import useT from "../hooks/useT"
import useAsync from "../hooks/useAsync"
import useSiteLanguage, { useCurrency } from "../hooks/useSiteLanguage"
import { ListAuthorProducts } from "../api"

// Up to three other products by the product's authors (author {products}): the
// server loads them with the page (pages/productSSR), so they are in the HTML a
// crawler reads; the browser loads them itself after a client-side navigation.
// The first three as the API ranks them, so the server and the browser agree.
// `exclude`: products another row of the page already shows (the series' books).
export default function MoreByAuthor({ product, exclude = null, className = "mb-[88px]" }) {
  const t = useT()
  const pageContext = usePageContext()
  const language = useSiteLanguage()
  const currency = useCurrency()
  const authors = product?.author || []
  const slugs = authors.map((a) => a.pid?.replace("author.", "") || a.slug).filter(Boolean)
  const key = slugs.join(",")
  const ssr = pageContext.storefrontAuthorProducts
  const seed = ssr && ssr.pid === product?.pid && ssr.language === language ? ssr.list : null

  const { result } = useAsync(
    async () => {
      const results = await Promise.all(
        slugs.map((slug) =>
          ListAuthorProducts({ slug, page: 0, items_per_page: 10, language, currency, sortBy: "recommendationScore" }).catch(() => null)
        )
      )
      const all = results.flatMap((r) => r?.list || [])
      return all.filter((p, i) => all.findIndex((q) => q.pid === p.pid) === i)
    },
    [key, language],
    { enabled: slugs.length > 0, initial: seed }
  )

  const picks = useMemo(() => {
    return (result || []).filter((p) => p.pid !== product?.pid && !exclude?.has(p.pid)).slice(0, 3)
  }, [result, product?.pid, exclude])

  if (!slugs.length || picks.length === 0) return null
  const authorName = product.author_display_name || authors.map((a) => a.name).join(", ")

  return (
    <section className={`w-full flex flex-col items-center ${className}`} data-testid="more-by-author">
      <h3 className="text-[24px] md:text-[28px] font-bold text-text text-center mb-10 m-0">{t("More by %1$s", authorName)}</h3>
      <div className="w-full overflow-x-auto no-scrollbar">
        <div className="flex flex-row flex-nowrap gap-4 md:gap-6 w-max mx-auto px-4 md:px-0">
          {picks.map((p) => (
            <ProductBox key={p.pid} product={p} placement="more_by_author" />
          ))}
        </div>
      </div>
    </section>
  )
}
